import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CiSearch } from "react-icons/ci";
import useApiFetcher from "../../hooks/useApiFetcher";
import Modal from "../Modal/Modal";
import { debounce } from "../Avatar/Utility";
import { useSelector } from "react-redux";
import { MdDelete } from "react-icons/md";
import { FaEdit } from "react-icons/fa";
import LoaderSpiner from "../LoaderSpiner";
import { useTheme } from "../BrandColors/Context/ThemeContext";

const UsersTable = () => {
  const Loggedinuser = useSelector((state) => state.auth.user);
  
  const delaershipTheme = useTheme();
  const user = JSON.parse(localStorage.getItem("user"));
  
  // theme color
  const theme = user?.role === "DealershipAdmin" ? delaershipTheme : "#115B5A";

  const { apiFetcher } = useApiFetcher();
  const [users, setusers] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [title, setTitle] = useState("Add new User");
  const [isModalOpen, setModalOpen] = useState(false);
  const [query, setquery] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [modulePermissions, setModulePermissions] = useState([]);
  const [Productpermissions, setproductPermissions] = useState([]);
  const [editing, setIsediting] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [isDebounceCompleted, setisDebounceCompleted] = useState(false);
  const [Products, setProducts] = useState([]);
  const [modules, setModules] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const itemsPerPage = 5;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredUsers?.length > 0 ? filteredUsers?.slice(indexOfFirstItem, indexOfLastItem) : users?.slice(indexOfFirstItem, indexOfLastItem);

  // Total pages calculation
  const totalPages = Math.ceil((filteredUsers?.length > 0 ? filteredUsers?.length : users?.length) / itemsPerPage) || 1;

  const columns = useMemo(() => [
    { _id: 1, name: "No" },
    { _id: 2, name: "Name" },
    { _id: 3, name: "Email Id" },
    { _id: 4, name: "Role" },
    { _id: 5, name: Loggedinuser.role === "Owner" ? "Assigned Modules" : "Assigned Products" },
    { _id: 6, name: "Action" },
  ], [Loggedinuser?.role]);

  const handlePermissionChange = (_id, permissionType, userRole) => {
    const stateMapping = {
      Owner: setModulePermissions,
      DealershipAdmin: setproductPermissions,
    };
    stateMapping?.[userRole]((prevpermissions) => {
      return prevpermissions?.map((item) => {
        if (item._id === _id) {
          const hasPermission = item?.permissions?.includes(permissionType);
          if (hasPermission) {
            return {
              ...item,
              permissions: item?.permissions?.filter(
                (perm) => perm !== permissionType
              ),
            };
          } else {
            return {
              ...item,
              permissions: [...item.permissions, permissionType],
            };
          }
        } else {
          return item;
        }
      })
    })
  };

  useEffect(() => {
    const fetchproducts = async () => {
      try {
        setLoading(true);
        const response = await apiFetcher("/products");
        const productWithPermissions = response?.map((product) => ({
          _id: product._id,
          product: product.name,
          permissions: [], // Initialize permissions as an empty array
        }));
        setproductPermissions(productWithPermissions)
        setProducts(response);
        setLoading(false);
      } catch (error) {
        console.error(error.errorMessage);
        setError("An error occurred while fetching users");
        setLoading(false);
      } finally {
        setLoading(false);
      }
    };
    fetchproducts()
  }, [])

  useEffect(() => {
    const fetchModules = async () => {
      try {
        setLoading(true);
        const response = await apiFetcher("/modules");
        const modulesWithPermissions = response?.map((module) => ({
          _id: module._id,
          module: module.name,
          permissions: [], // Initialize permissions as an empty array
        }));
        setModules(response);
        setModulePermissions(modulesWithPermissions)
        setLoading(false);
      } catch (error) {
        setError("An error occurred while fetching users");
        setLoading(false);
      } finally {
        setLoading(false);
      }
    }
    fetchModules()
  }, [])

  const fetchUserByname = (query) => {
    if (!query) {
      setFilteredUsers([]);
      setisDebounceCompleted(false);
      return;
    }
    const filteredNames = users?.filter((user) => {
      return user.email
        .split("@")[0]
        .toLowerCase()
        .includes(query.toLowerCase());
    });
    setFilteredUsers(filteredNames);
    setisDebounceCompleted(true);
  };

  const openModal = () => {
    setModalOpen(true);
  };

  const clearInputfields = () => {
    setIsediting(false);
    setEditingUserId(null);
    setTitle("Add new User");
    setEmail("");
    setPassword("");
    let resetPermissionsforModules = modulePermissions?.map((module) => ({
      _id: module._id,
      module: module.module,
      permissions: [],
    }));
    let resetPermissionsforProducts = Productpermissions?.map((product) => ({
      _id: product._id,
      product: product.product,
      permissions: [],
    }))
    if (Loggedinuser.role === "Owner") {
      setModulePermissions(resetPermissionsforModules);
    } else {
      setproductPermissions(resetPermissionsforProducts);
    }
  }

  const closeModal = () => {
    setModalOpen(false);
    clearInputfields();
  };

  const handleEdit = async (user) => {
    setModalOpen(true);
    setIsediting(true);
    setEditingUserId(user._id);
    setTitle("Edit user");
    setEmail(user.email);
    setPassword(user.password);
    let formattedStructureforModules = user?.assignedModules?.map((module) => ({
      _id: module?.module?._id,
      module: module?.module?.name,
      permissions: module?.permissions, 
    }));
    let formattedStructureforProducts = user?.assignedProducts?.map((product) => ({
      _id: product?.product?._id,
      product: product?.product?.name,
      permissions: product?.permissions,
    }))
    if (Loggedinuser.role === "Owner") {
      setModulePermissions(formattedStructureforModules);
    } else {
      setproductPermissions(formattedStructureforProducts);
    }
  };

  const handleDelete = async (user) => {
    try {
      await apiFetcher(`/users/${user._id}`, "DELETE");
      setModalOpen(false);
      fetchUsers();
    } catch (error) {
      console.log(error.message, "some error occured");
    }
  };

  const addNewOrUpdateUser = async () => {
    setError(""); //clear prev errors
    const url = editing
      ? `/users/${editingUserId}`
      : `/users`;
    const method = editing ? "PATCH" : "POST";
    const payload = {
      email,
      password,
      assignedModules:
        Loggedinuser?.role === "Owner"
          ? modulePermissions?.map(({ _id, permissions }) => ({
            module: _id,
            permissions,
          }))
          : [],
      assignedProducts:
        Loggedinuser?.role === "DealershipAdmin"
          ? Productpermissions?.map(({ _id, permissions }) => ({
            product: _id,
            permissions,
          }))
          : [],
    };
    try {
      await apiFetcher(url, method, payload);
      fetchUsers();
      clearInputfields();
      setModalOpen(false);
    } catch (error) {
      console.error("error failed:", error.message);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await apiFetcher("/users");
      setusers(response);
      setLoading(false);
    } catch (error) {
      setError("An error occurred while fetching users", error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const debouncedfn = useCallback(debounce(fetchUserByname, 300), [users]);

  useEffect(() => {
    debouncedfn(query);
  }, [query]);

  const renderTable = (usersData) => {
    return (
      usersData.length > 0 &&
      usersData?.map((userData, index) => {
        const sequentialId = (currentPage - 1) * itemsPerPage + (index + 1);

        return (
          <tr key={userData._id} className="text-slate-700 text-sm hover:bg-white/5 transition-colors group border-b border-blue-200/50">
            <td className="px-4 py-4">{sequentialId}</td>
            <td className="px-4 py-4 font-medium text-slate-800">{userData.email.split("@")[0]}</td>
            <td className="px-4 py-4">{userData.email}</td>
            <td className="px-4 py-4">
              <span className="px-2 py-1 bg-blue-100 rounded-md text-slate-700 text-xs font-medium">
                 {userData.role}
              </span>
            </td>
            {Loggedinuser.role === "Owner" ? (
              <td className="px-4 py-4 whitespace-nowrap">
                <div className="flex flex-wrap gap-2">
                  {userData?.assignedModules?.length > 0 ? (
                    userData.assignedModules.map((module) =>
                      module?.permissions.length !== 0 && (
                        <span
                          key={module?.module?._id}
                          className="text-xs font-medium px-2 py-1 bg-primary-900/30 text-primary-300 rounded border border-primary-500/20"
                        >
                          {module?.module?.name}
                        </span>
                      )
                    )
                  ) : (
                    <span className="text-xs text-slate-500 italic">
                      No modules assigned
                    </span>
                  )}
                </div>
              </td>
            ) : (
              <td className="px-4 py-4 whitespace-nowrap">
                <div className="flex flex-wrap gap-2">
                  {userData?.assignedProducts?.length > 0 ? (
                    userData.assignedProducts.map((product) =>
                      product?.permissions.length !== 0 && (
                        <span
                          key={product?.product?._id}
                          className="text-xs font-medium px-2 py-1 bg-primary-900/30 text-primary-300 rounded border border-primary-500/20"
                        >
                          {product?.product?.name}
                        </span>
                      )
                    )
                  ) : (
                    <span className="text-xs text-slate-500 italic">
                      No products assigned
                    </span>
                  )}
                </div>
              </td>
            )}
            <td className="px-4 py-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleEdit(userData)}
                  className="text-primary-400 hover:text-primary-300 hover:scale-110 transition-transform p-1.5 rounded-full hover:bg-primary-900/30"
                  title="Edit"
                >
                  <FaEdit size={16} />
                </button>
                <button
                  onClick={() => handleDelete(userData)}
                  className="text-red-400 hover:text-red-300 hover:scale-110 transition-transform p-1.5 rounded-full hover:bg-red-900/30"
                  title="Delete"
                >
                  <MdDelete size={16} />
                </button>
              </div>
            </td>
          </tr>
        );
      })
    );
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  return (
    <div className="glass-panel w-full flex flex-col p-6 animate-fade-in-up mt-8 min-h-[60vh]">
      
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <h2 className="text-2xl font-bold text-slate-800">Manage Users</h2>
        
        <div className="flex flex-col md:flex-row items-center gap-4 w-full md:w-auto">
          <div className="relative w-full md:w-72">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-600">
              <CiSearch size={20} />
            </div>
            <input
              type="text"
              value={query}
              onChange={(event) => setquery(event.target.value)}
              className="input-field pl-10 w-full"
              placeholder="Search for user name..."
            />
          </div>
          
          <button 
            onClick={openModal} 
            className="w-full md:w-auto btn-primary whitespace-nowrap shadow-lg flex items-center gap-2 justify-center" 
            style={{ backgroundColor: theme }}
          >
            <span className="text-lg leading-none mb-0.5">+</span> Add New User
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-20 flex-1">
          <LoaderSpiner />
        </div>
      ) : error ? (
        <div className="text-red-400 font-medium bg-red-900/20 p-4 rounded-lg text-center mt-4">{error}</div>
      ) : (
        <div className="flex-1 flex flex-col">
          <div className="w-full overflow-x-auto rounded-lg border border-blue-200/50 scrollbar-hide">
            <table className="w-full text-left border-collapse min-w-max">
              <thead>
                <tr className="text-white text-sm font-semibold whitespace-nowrap" style={{ backgroundColor: theme }}>
                  {columns?.map(({ _id, name }, index) => {
                     let roundedClass = "";
                     if(index === 0) roundedClass = "rounded-tl-lg";
                     if(index === columns.length - 1) roundedClass = "rounded-tr-lg";
                     return <th key={_id} className={`px-4 py-4 ${roundedClass}`}>{name}</th>;
                  })}
                </tr>
              </thead>
              <tbody className="bg-white/30">
                {isDebounceCompleted && filteredUsers.length === 0 && query !== "" ? (
                  <tr>
                    <td colSpan={columns.length} className="px-4 py-12 text-center text-slate-500">No results found matching "{query}"</td>
                  </tr>
                ) : filteredUsers.length === 0 && currentItems.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="px-4 py-12 text-center text-slate-500">No users found.</td>
                  </tr>
                ) : filteredUsers.length === 0 ? (
                  renderTable(currentItems)
                ) : (
                  renderTable(filteredUsers)
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!isLoading && !error && totalPages > 1 && (
            <div className="flex justify-center items-center mt-6 gap-2 pt-4">
              <button
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
                className="px-4 py-2 rounded-lg font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-white text-slate-700 hover:bg-blue-100"
              >
                Prev
              </button>
              
              <div className="flex gap-1">
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i}
                    onClick={() => handlePageChange(i + 1)}
                    className={`w-10 h-10 rounded-lg flex items-center justify-center font-medium text-sm transition-all ${
                      currentPage === i + 1 
                        ? 'text-white shadow-lg scale-105'
                        : 'bg-white text-slate-600 hover:bg-blue-100 hover:text-white'
                    }`}
                    style={currentPage === i + 1 ? { backgroundColor: theme } : {}}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
              
              <button
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
                className="px-4 py-2 rounded-lg font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-white text-slate-700 hover:bg-blue-100"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {/* Modal - Applying Tailwind classes for modal internals */}
      <Modal isOpen={isModalOpen} closeModal={closeModal} title={title}>
        <div className="flex flex-col gap-6 w-full max-w-md mx-auto p-2">
          
          <div className="flex flex-col gap-1.5">
            <label htmlFor="email" className="text-sm font-medium text-slate-700">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              className="input-field"
              placeholder="user@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="password" className="text-sm font-medium text-slate-700">
              Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              required
              className="input-field"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-3 mt-2">
            <label className="text-sm font-bold text-slate-800 border-b border-blue-200 pb-2">
              {Loggedinuser.role === "Owner" ? "Module Permissions" : "Product Permissions"}
            </label>
            
            <div className="flex flex-col gap-3 max-h-60 overflow-y-auto pr-2 scrollbar-hide">
              {Loggedinuser.role === "Owner"
                ? modulePermissions?.map(({ _id, module, permissions }) => (
                  <div key={_id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-blue-200/50">
                    <span className="font-medium text-slate-800">{module}</span>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={permissions?.includes("read")}
                          onChange={() => handlePermissionChange(_id, "read", "Owner")}
                          className="w-4 h-4 rounded bg-blue-50 border-slate-600 text-primary-500 focus:ring-primary-500 focus:ring-offset-dark-800 cursor-pointer"
                        />
                        <span className="text-sm text-slate-600 group-hover:text-slate-700 transition-colors">Read</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={permissions?.includes("write")}
                          onChange={() => handlePermissionChange(_id, "write", "Owner")}
                          className="w-4 h-4 rounded bg-blue-50 border-slate-600 text-primary-500 focus:ring-primary-500 focus:ring-offset-dark-800 cursor-pointer"
                        />
                        <span className="text-sm text-slate-600 group-hover:text-slate-700 transition-colors">Write</span>
                      </label>
                    </div>
                  </div>
                ))
                : Productpermissions?.map(({ _id, product, permissions }) => (
                  <div key={_id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-blue-200/50">
                    <span className="font-medium text-slate-800">{product}</span>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={permissions?.includes("read")}
                          onChange={() => handlePermissionChange(_id, "read", "DealershipAdmin")}
                          className="w-4 h-4 rounded bg-blue-50 border-slate-600 text-primary-500 focus:ring-primary-500 focus:ring-offset-dark-800 cursor-pointer"
                        />
                        <span className="text-sm text-slate-600 group-hover:text-slate-700 transition-colors">Read</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={permissions?.includes("write")}
                          onChange={() => handlePermissionChange(_id, "write", "DealershipAdmin")}
                          className="w-4 h-4 rounded bg-blue-50 border-slate-600 text-primary-500 focus:ring-primary-500 focus:ring-offset-dark-800 cursor-pointer"
                        />
                        <span className="text-sm text-slate-600 group-hover:text-slate-700 transition-colors">Write</span>
                      </label>
                    </div>
                  </div>
                ))}
            </div>
          </div>

        </div>

        <div className="flex justify-end gap-3 mt-8 pt-4 border-t border-blue-200/50 w-full">
          <button 
            className="px-5 py-2.5 rounded-lg font-medium text-slate-700 bg-white hover:bg-blue-100 transition-colors border border-blue-200" 
            onClick={closeModal}
          >
            Cancel
          </button>
          <button 
            onClick={addNewOrUpdateUser} 
            className="px-5 py-2.5 rounded-lg font-medium text-white shadow-lg transition-transform hover:-translate-y-0.5 active:translate-y-0"
            style={{ backgroundColor: theme }}
          >
            {!editing ? "Add User" : "Save Changes"}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default UsersTable;
